import { useEffect, useState } from 'react';

export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

const loadWith = (src: string, crossOrigin: string | null): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  if (crossOrigin) image.crossOrigin = crossOrigin;
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Nie udało się wczytać obrazu'));
  image.src = src;
});

/**
 * Hosty znakowanych podglądów ze stocka. Tylko dla nich schodzimy z prośby
 * o zgodę na użycie w pliku.
 */
const PREVIEW_HOSTS = ['img.freepik.com', 'img.magnific.com'];

const isPreviewHost = (src: string): boolean => PREVIEW_HOSTS.some((host) => src.includes(host));

/**
 * Wczytuje obraz, prosząc o zgodę na użycie w pliku (`crossOrigin`).
 *
 * Bez tej zgody przeglądarka „skaża" płótno i blokuje eksport CAŁEJ kreacji,
 * nie tylko tego jednego zdjęcia. Dlatego ponowienie bez `crossOrigin` robimy
 * WYŁĄCZNIE dla podglądów ze stocka: tam znak wodny i tak wyklucza publikację,
 * więc utrata eksportu niczego nie psuje. Dla naszego CDN wolimy pustą ramkę
 * i działający eksport niż odwrotnie.
 */
export const loadImage = async (src: string): Promise<HTMLImageElement> => {
  try {
    return await loadWith(src, 'anonymous');
  } catch (error) {
    if (!isPreviewHost(src)) throw error;
    return loadWith(src, null);
  }
};

/** Wczytuje obraz do elementu HTML, którego oczekuje węzeł Konvy. */
export const useImageElement = (src: string): HTMLImageElement | null => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!src) {
      setImage(null);
      return undefined;
    }
    let active = true;
    loadImage(src)
      .then((loaded) => {
        if (active) setImage(loaded);
      })
      .catch(() => {
        if (active) setImage(null);
      });
    return () => {
      active = false;
    };
  }, [src]);

  return image;
};
